import { TFunction } from 'i18next';
import { MetricDefinition, PHYSICAL_SOURCE } from '@/types/metric';
import { NestedObject } from '@/types/object';
import { CellDataValue, EnrichedTableRow } from '@/utils/atoms/enrichTableData';
import { isNullish } from '@/utils/predicates';

export const getPhysicalNullTooltip = (t: TFunction, isPhysical: boolean, value: CellDataValue): string | undefined =>
  isPhysical && isNullish(value) ? t('noPhysicalData', { ns: 'table' }) : undefined;

export const calculate360MetricKeys = (metricData: MetricDefinition[]) =>
  new Set(
    metricData.reduce<string[]>((acc, metric) => {
      if (metric?.is360 && typeof metric?.key === 'string') acc.push(metric.key);
      return acc;
    }, []),
  );

export const calculatePhysicalMetricKeys = (metricData: MetricDefinition[]) =>
  new Set(metricData.filter(metric => metric.source === PHYSICAL_SOURCE).map(metric => metric.key));

// Extract value from either CellData or value.
// Required until we complete work to support totals in Game and Team AggregateTables
const getValue = (data: CellDataValue) =>
  !isNullish(data) && typeof data === 'object' && 'value' in data ? data.value : data;

export const getAsteriskAndTooltipFor360Metric = (
  t: TFunction,
  metricKey: string | undefined,
  threeSixtyMetricKeys: Set<string>,
  minutesPlayed: number,
  minutesPlayed360: number,
) => {
  const is360Metric = metricKey && threeSixtyMetricKeys.has(metricKey);
  if (!is360Metric || minutesPlayed360 === 0) return { tooltipContent: undefined, showAsteriskWithTooltip: false };

  if (minutesPlayed !== minutesPlayed360)
    return {
      tooltipContent: t('valueFromThreeSixtyCyclesOnly', { ns: 'table' }),
      showAsteriskWithTooltip: true,
    };

  return { tooltipContent: undefined, showAsteriskWithTooltip: false };
};

export const calculateTooltipAndAsterisk = (
  t: TFunction,
  metricKey: undefined | string,
  threeSixtyMetricKeys: Set<string>,
  row: EnrichedTableRow | NestedObject,
  no360DataKey: 'no360Data' | 'no360DataForGame',
) => {
  if (!metricKey || !threeSixtyMetricKeys.has(metricKey))
    return { tooltipContent: undefined, showAsteriskWithTooltip: false };

  const minutesPlayed = getValue(row['aggregates.minutesPlayed']);
  const minutesPlayed360 = getValue(row['aggregates.minutesPlayed360']);

  if (
    isNullish(minutesPlayed) ||
    isNullish(minutesPlayed360) ||
    typeof minutesPlayed !== 'number' ||
    typeof minutesPlayed360 !== 'number' ||
    Number.isNaN(minutesPlayed) ||
    Number.isNaN(minutesPlayed360)
  ) {
    return { tooltipContent: undefined, showAsteriskWithTooltip: false };
  }

  if (minutesPlayed360 === 0)
    return { tooltipContent: t(no360DataKey, { ns: 'table' }), showAsteriskWithTooltip: false };

  return getAsteriskAndTooltipFor360Metric(t, metricKey, threeSixtyMetricKeys, minutesPlayed, minutesPlayed360);
};

export const resolveCellTooltip = (
  t: TFunction,
  metricKey: string | undefined,
  threeSixtyMetricKeys: Set<string>,
  row: EnrichedTableRow | NestedObject,
  no360DataKey: 'no360Data' | 'no360DataForGame',
  { isPhysical, value }: { isPhysical: boolean; value: CellDataValue },
): { tooltipContent: string | undefined; showAsteriskWithTooltip: boolean } => {
  const result = calculateTooltipAndAsterisk(t, metricKey, threeSixtyMetricKeys, row, no360DataKey);
  return { ...result, tooltipContent: result.tooltipContent ?? getPhysicalNullTooltip(t, isPhysical, value) };
};
